/**
 * Workspace Activity — frontend entity (Patch 36.0 · Project Obsidian).
 *
 * The Recent Activity panel is a pure renderer of
 * ``GET /api/v1/workspaces/{workspace_id}/activity``. The client must
 * not merge, dedupe, rank, or interpret activity — the backend is the
 * single source of truth.
 */
import {
  useInfiniteQuery,
  useQuery,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { http } from "@/shared/api/http";

// ─── Types (mirror app.services.workspace_activity_service) ─────────
export type ActivityType =
  | "business_brain_approved"
  | "campaign_created"
  | "campaign_completed"
  | "asset_rendered"
  | "asset_approved"
  | "asset_vaulted"
  | "publication_scheduled"
  | "publication_published"
  | "publication_failed"
  | "publication_recovered"
  | "akinex_recommendation_accepted"
  | "akinex_recommendation_dismissed";

export interface ActivityAction {
  path: string;
  label: string;
}

export interface ActivityItem {
  id: string;
  type: ActivityType;
  title: string;
  description: string;
  occurred_at: string;
  workspace_id: string;
  entity_type: string;
  entity_id: string;
  status: string;
  metadata: Record<string, unknown>;
  action: ActivityAction | null;
}

export interface ActivityFeed {
  workspace_id: string;
  generated_at: string;
  limit: number;
  count: number;
  items: ActivityItem[];
  next_cursor: string | null;
  supported_types: ActivityType[];
}

// ─── API + hook ─────────────────────────────────────────────────────
export const activityKeys = {
  feed: (workspaceId: string, limit: number, cursor: string | null) =>
    ["workspace-activity", workspaceId, limit, cursor ?? ""] as const,
};

export function fetchWorkspaceActivity(
  workspaceId: string,
  { limit = 10, cursor = null }: { limit?: number; cursor?: string | null } = {},
): Promise<ActivityFeed> {
  const q = new URLSearchParams({ limit: String(limit) });
  if (cursor) q.set("cursor", cursor);
  return http<ActivityFeed>(
    `/workspaces/${workspaceId}/activity?${q.toString()}`,
  );
}

export function useWorkspaceActivity(
  workspaceId: string | null,
  { limit = 10 }: { limit?: number } = {},
): UseQueryResult<ActivityFeed, unknown> {
  return useQuery({
    queryKey: workspaceId
      ? activityKeys.feed(workspaceId, limit, null)
      : ["workspace-activity", "no-workspace"],
    queryFn: () => fetchWorkspaceActivity(workspaceId as string, { limit }),
    enabled: !!workspaceId,
    staleTime: 30_000,
    refetchInterval: 120_000,
    retry: false,
  });
}

/**
 * Patch 36.0.1 · Activity Load More.
 *
 * Infinite-cursor version of the feed. Uses the same backend endpoint
 * — the client never reranks or deduplicates. Each page's items are
 * concatenated in server-emitted order. The reader below flattens
 * pages into a single array for rendering.
 */
export function useInfiniteWorkspaceActivity(
  workspaceId: string | null,
  { limit = 10 }: { limit?: number } = {},
): UseInfiniteQueryResult<
  { pages: ActivityFeed[]; pageParams: (string | null)[] },
  unknown
> {
  return useInfiniteQuery({
    queryKey: workspaceId
      ? ["workspace-activity-infinite", workspaceId, limit]
      : ["workspace-activity-infinite", "no-workspace"],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      fetchWorkspaceActivity(workspaceId as string, {
        limit, cursor: pageParam as string | null,
      }),
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: !!workspaceId,
    staleTime: 30_000,
    refetchInterval: false,
    retry: false,
  });
}

/** Flatten paginated pages into a single item list preserving server
 *  ordering. Pure helper — no mutation, no ranking. */
export function flattenActivityPages(
  data: { pages: ActivityFeed[] } | undefined,
): ActivityItem[] {
  if (!data) return [];
  const out: ActivityItem[] = [];
  for (const p of data.pages) out.push(...p.items);
  return out;
}
